import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import WfYearGrid from './WfYearGrid';
import WfTargets from './WfTargets';
import { downloadWfWorksheetPdf } from '../utils/wfWorksheetPdf';
import { worksheetPnl, worksheetYear } from './wfWorksheetCalc';
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const WfWorksheet = forwardRef(function WfWorksheet({ year, onPickMonth }, ref) {
  const [data, setData] = useState(null), [drafts, setDrafts] = useState({});
  const [message, setMessage] = useState('Click an amount to edit. Changes save when you leave the cell.');
  const [error, setError] = useState('');
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const pending = useRef({}), saving = useRef(null);
  useEffect(() => {
    let live = true;
    setData(null);
    axios.get(`/api/wf-report/year/${year}`, auth()).then(r => { if (live) setData(r.data); })
      .catch(() => { if (live) setError('Could not load the worksheet. Reopen the worksheet to retry.'); });
    return () => { live = false; };
  }, [year]);
  const flush = useCallback(() => {
    if (saving.current) return saving.current;
    const run = async () => {
      while (Object.keys(pending.current).length) {
        const [month, patch] = Object.entries(pending.current)[0];
        const invalid = Object.values(patch).some(v => v !== '' && !Number.isFinite(Number(v)));
        if (invalid) { setError('Enter a valid number before leaving the worksheet.'); return false; }
        const sent = { ...patch };
        setMessage('Saving worksheet…'); setError('');
        try {
          const r = await axios.patch(`/api/wf-report/${month}/pnl`, sent, auth());
          setData(prev => ({ ...prev, months: prev.months.map(m => m.month === month ? { ...m, status: r.data.status, pnl: worksheetPnl(r.data.pnl.lines) } : m) }));
          const remaining = { ...pending.current[month] };
          Object.keys(sent).forEach(k => { if (remaining[k] === sent[k]) delete remaining[k]; });
          const next = { ...pending.current };
          if (Object.keys(remaining).length) next[month] = remaining; else delete next[month];
          pending.current = next; setDrafts(next);
        } catch (e) { setError(e.response?.data?.error || 'Save failed. Your edits are still here. Retry before leaving.'); return false; }
      }
      setMessage('All worksheet changes saved');
      return true;
    };
    saving.current = run().finally(() => { saving.current = null; });
    return saving.current;
  }, []);
  useImperativeHandle(ref, () => ({ flush }), [flush]);
  useEffect(() => {
    const warn = e => { if (saving.current || Object.keys(pending.current).length) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', warn);
    return () => { window.removeEventListener('beforeunload', warn); flush(); };
  }, [flush]);
  const edit = (month, key, value) => {
    const next = { ...pending.current, [month]: { ...pending.current[month], [key]: value } };
    pending.current = next; setDrafts(next); setMessage('Unsaved changes');
  };
  const downloadPdf = async () => {
    if (exporting) return;
    setExporting(true); setExportError('');
    try {
      if (!(await flush())) return;
      const response = await axios.get(`/api/wf-report/year/${year}`, auth());
      await downloadWfWorksheetPdf(response.data);
    } catch (e) {
      setExportError('Could not download the PDF. Please try again.');
    } finally { setExporting(false); }
  };
  const view = useMemo(() => data && worksheetYear(data, drafts), [data, drafts]);
  return <>
    <div className="wf-worksheet-export">
      <span className="wf-hint">PDF includes January–December and YTD · A3 landscape</span>
      <button className="wf-btn primary" onClick={downloadPdf} disabled={!view || exporting}>{exporting ? 'Preparing PDF…' : 'Download PDF'}</button>
    </div>
    {exportError && <p className="wf-error" role="alert">{exportError}</p>}
    <div className="wf-worksheet-status" role="status">{error ? <><span className="wf-error">{error}</span><button className="wf-btn" onClick={flush}>Retry save</button></> : message}</div>
    {view && <WfYearGrid year={year} suppliedData={view} drafts={drafts} onEdit={edit} onBlur={flush} onPickMonth={async month => { if (await flush()) onPickMonth(month); }} />}
    {!view && !error && <p className="wf-sub">Loading worksheet…</p>}
    <p className="wf-hint">Amounts in RM · totals and YTD calculate automatically · final months are locked; open their shareholder report to unlock.</p>
  </>;
});
const WfWorksheetViews = forwardRef(function WfWorksheetViews(props, ref) {
  const [view,setView]=useState('actual'); const inner=useRef();
  useImperativeHandle(ref,()=>({flush:()=>inner.current?.flush() ?? Promise.resolve(true)}));
  const switchView=async next=>{if(await (inner.current?.flush() ?? Promise.resolve(true)))setView(next);};
  return <><div className="wf-worksheet-export" role="tablist" aria-label="Worksheet figures">{[['actual','Actual figures'],['target','Enter targets'],['difference','Actual − Target']].map(([key,label])=><button className={`wf-btn ${view===key?'primary':''}`} key={key} role="tab" aria-selected={view===key} onClick={()=>switchView(key)}>{label}</button>)}</div>{view==='actual'?<WfWorksheet {...props} ref={inner}/>:<WfTargets key={`${props.year}-${view}`} year={props.year} comparison={view==='difference'} ref={inner}/>}</>;
});
export default WfWorksheetViews;
